class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-short', FacetShort, { extends: 'select' });

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    /* CUSTOM: harempants — decouple user intent from remote state. `renderSeq` is a
       monotonic request id so only the LATEST filter request may paint (kills the
       out-of-order race); `inflight` is the AbortController for the current fetch so
       a superseded request is cancelled. See
       plans/research/collection-filter-race-condition-2026-07.md. */
    this.renderSeq = 0;
    this.inflight = null;
    /* END CUSTOM: harempants */

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange(event) {
    this.dirty = true;
    /* CUSTOM: harempants — remember which filter group the shopper is actively editing.
       The real change event's target is the clicked control, so capture its group and
       carry it on the dispatched submit event; renderFilters then SKIPS re-rendering
       that group. Skipping avoids destroying the control under the shopper's finger —
       no reflow, no lost focus / show-more / scroll state (the "lag" felt when removing
       a checkmark). reconcileControls() still fixes its checked-state. The vendor meant
       to do this (matchesIndex) but read the synthetic submit event's target (the form),
       so it never worked. */
    const group = event && event.target && event.target.closest ? event.target.closest('[data-filter]') : null;
    const submitEvent = new Event('submit', { cancelable: true });
    submitEvent.activeFilterIndex = group ? group.dataset.index : null;
    this.dispatchEvent(submitEvent);
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  /* CUSTOM: harempants — true when the mobile filter drawer is open. Used to skip the
     background scroll while the shopper is looking at the drawer, not the grid. */
  isDrawerOpen() {
    const drawer = document.getElementById('FacetDrawer');
    return !!(drawer && !drawer.hasAttribute('hidden'));
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    if (container) {
      FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);
    }

    /* CUSTOM: harempants — show the loading state immediately. Vendor delayed it 100ms,
       so on fast renders the spinner appeared AFTER the content. */
    loadings.forEach((loading) => loading.classList.add('btn--loading'));

    /* CUSTOM: harempants — only re-anchor to the top of the results for INLINE
       (desktop sidebar) filtering. When the mobile filter drawer is open the shopper
       is looking at the drawer, so scrolling the background is jarring (Bug 4). */
    if (!this.isDrawerOpen()) {
      const target = document.querySelector('.collection');
      if (target) {
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - 200,
          behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth'
        });
      }
    }
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) }
      ]
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event) {
    /* CUSTOM: harempants — user intent is authoritative. Update the URL + clear the
       dirty flag synchronously (optimistic), then stamp this request with a monotonic
       seq and abort any in-flight fetch so ONLY the latest request can paint. This is
       the fix for the out-of-order race (Bugs 1 & 3). Vendor fired a bare fetch per
       change with no sequencing, so a stale response could land last and win. */
    if (this.hasAttribute('data-history')) this.updateURLHash(url);
    this.dirty = false;

    /* CUSTOM: harempants — the group the shopper is editing (from onFormChange), so the
       render can skip it. null for sort/facet-remove → all groups render. */
    const activeIndex = event && event.activeFilterIndex ? event.activeFilterIndex : null;

    const seq = ++this.renderSeq;
    if (this.inflight) {
      this.inflight.abort();
      this.inflight = null;
    }

    if (this.cachedMap.has(url)) {
      this.renderSectionFromCache(url, event, seq, activeIndex);
    } else {
      const controller = 'AbortController' in window ? new AbortController() : null;
      this.inflight = controller;
      this.renderSectionFromFetch(url, event, seq, controller ? controller.signal : undefined, activeIndex);
    }
  }

  renderSectionFromFetch(url, event, seq, signal, activeIndex) {
    this.beforeRenderSection();

    fetch(url, signal ? { signal } : undefined)
      .then((response) => response.text())
      .then((responseText) => {
        /* CUSTOM: harempants — drop superseded responses; only the latest seq paints. */
        if (seq !== this.renderSeq) return;
        /* CUSTOM: harempants — parse the (large) section response ONCE, not 8× (perf). */
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.applyResponse(parsedHTML, url, responseText, activeIndex);
        this.cachedMap.set(url, responseText);
        this.trimCache();
        this.afterRenderSection();
      })
      .catch((error) => {
        /* Superseded fetches are aborted on purpose — ignore. For a real failure,
           un-stick the UI (vendor left the grid faded + spinner spinning forever). */
        if (error && error.name === 'AbortError') return;
        if (seq === this.renderSeq) this.afterRenderSection();
      });
  }

  renderSectionFromCache(url, event, seq, activeIndex) {
    this.beforeRenderSection();

    /* CUSTOM: harempants — no artificial 250ms floor. Paint on the next frame so the
       fade-out is visible, then apply — a cache hit is now near-instant (~470ms → tens
       of ms). Still seq-guarded so a stale cache render can't win. */
    requestAnimationFrame(() => {
      if (seq !== this.renderSeq) return;
      const responseText = this.cachedMap.get(url);
      const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
      this.applyResponse(parsedHTML, url, responseText, activeIndex);
      this.afterRenderSection();
    });
  }

  /* CUSTOM: harempants — apply one parsed response to the page. Results (grid, count,
     pills, sort) come from the server; renderFilters patches each group's option
     counts/availability IN PLACE (never replacing control nodes); reconcileControls()
     then forces the shopper's own selection back onto every control, so a server echo
     can never revert a tick ("what the user clicks, the user gets").
     NOTE: renderFacetFiltersContainer (which wholesale-replaced the desktop sidebar —
     destroying its <form>, cache and control nodes on every render) is intentionally
     NOT called: renderFilters already keeps the desktop groups in sync surgically, so
     the wholesale replace was redundant churn. */
  applyResponse(parsedHTML, url, responseText, activeIndex) {
    /* CUSTOM: harempants — paint the RESULTS first (what the shopper is waiting to see),
       then patch the filter groups (secondary) so the surgical count patching never
       delays the grid update. */
    this.renderProductGridContainer(parsedHTML);
    this.renderProductCount(parsedHTML);
    this.renderFiltersActive(parsedHTML);
    this.renderSortBy(parsedHTML);
    this.renderSortByMobile(parsedHTML);
    this.renderFilters(parsedHTML, activeIndex);
    this.reconcileControls(url);

    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, {
      responseText: responseText,
      parsedHTML: parsedHTML
    });
  }

  /* CUSTOM: harempants — the shopper's selection = the filter.* params in the just-
     applied URL (built from the firing form's FormData). Force every filter checkbox
     across all facet forms (drawer + desktop) to match it, overriding the server's
     rendered checked-state. Fixes the checkbox-revert (Bug 2) and keeps the drawer and
     desktop sidebar in sync. */
  reconcileControls(url) {
    const desired = new Map();
    new URL(url).searchParams.forEach((value, key) => {
      if (key.indexOf('filter.') !== 0) return;
      if (!desired.has(key)) desired.set(key, new Set());
      desired.get(key).add(value);
    });

    document
      .querySelectorAll('form[is="facet-form"] input[type="checkbox"][name^="filter."]')
      .forEach((input) => {
        const want = desired.has(input.name) && desired.get(input.name).has(input.value);
        if (input.checked !== want) input.checked = want;
      });
  }

  /* CUSTOM: harempants — cap the response cache; vendor never evicted (unbounded memory). */
  trimCache() {
    const MAX_CACHE = 60;
    while (this.cachedMap.size > MAX_CACHE) {
      this.cachedMap.delete(this.cachedMap.keys().next().value);
    }
  }

  /* CUSTOM: harempants — takes a pre-parsed Document (parse-once) and the data-index of
     the group the shopper is actively editing. Root-cause fix for the whole class of
     filter-drawer bugs: the shopper OWNS the controls (they toggle them locally), so we
     never let the server response replace a control node. patchFilterGroup() updates each
     group's option counts + disabled state IN PLACE — no innerHTML swap — which preserves
     the control nodes, keyboard focus, and each group's "show more"/scroll state, on BOTH
     the drawer and the desktop sidebar. reconcileControls() owns the checked-state. We only
     fall back to a full innerHTML swap when a group's option SET actually changed (rare),
     and even then never for the group the shopper is editing. */
  renderFilters(parsedHTML, activeIndex) {
    const selector =
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]';
    const responseFacets = parsedHTML.querySelectorAll(selector);
    const domFacets = document.querySelectorAll(selector);

    /* CUSTOM: harempants — hide groups absent from the response. Vendor keyed on
       element.id (always '' here → no-op); key on data-index. */
    const responseIndexes = new Set(Array.from(responseFacets).map((element) => element.dataset.index));
    domFacets.forEach((currentElement) => {
      if (!responseIndexes.has(currentElement.dataset.index)) {
        currentElement.classList.add('hidden');
      }
    });

    responseFacets.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter === null) return;
      filter.classList.remove('hidden');

      /* CUSTOM: harempants — surgical in-place patch (preserves nodes/focus/show-more). */
      if (this.patchFilterGroup(filter, element)) return;

      /* Fallback (option set changed): full swap. Never destroy the active group. */
      if (activeIndex && element.dataset.index === activeIndex) return;
      if (filter.tagName === 'DETAILS') {
        const domContent = filter.querySelector('summary + *');
        const responseContent = element.querySelector('summary + *');
        if (domContent && responseContent) domContent.innerHTML = responseContent.innerHTML;
      } else {
        filter.innerHTML = element.innerHTML;
      }
    });
  }

  /* CUSTOM: harempants — patch one filter group's option counts + disabled state in place
     from the response, WITHOUT replacing any control node. Returns true if fully patched;
     false (→ caller falls back to a swap) for price groups or when the option value-set
     changed. Never touches `checked` (reconcileControls owns intent) or the show-more
     hidden classes (the shopper owns expansion). */
  patchFilterGroup(domGroup, responseGroup) {
    const domInputs = Array.from(domGroup.querySelectorAll('input[name^="filter."]'));
    const responseInputs = Array.from(responseGroup.querySelectorAll('input[name^="filter."]'));

    // Only checkbox/swatch groups are patchable; price ranges fall back to a swap.
    if (!domInputs.length || !domInputs.every((input) => input.type === 'checkbox')) return false;
    if (domInputs.length !== responseInputs.length) return false;

    const responseByValue = new Map();
    responseInputs.forEach((input) => {
      const li = input.closest('li');
      const countEl = li ? li.querySelector('.count') : null;
      responseByValue.set(input.value, { disabled: input.disabled, count: countEl ? countEl.textContent : null });
    });
    // Value sets must line up for an in-place patch.
    for (const input of domInputs) if (!responseByValue.has(input.value)) return false;

    domInputs.forEach((input) => {
      const info = responseByValue.get(input.value);
      if (input.disabled !== info.disabled) input.disabled = info.disabled;
      if (info.count !== null) {
        const li = input.closest('li');
        const countEl = li ? li.querySelector('.count') : null;
        if (countEl && countEl.textContent !== info.count) countEl.textContent = info.count;
      }
    });
    return true;
  }

  renderFiltersActive(parsedHTML) {
    const id = 'FacetFiltersActive';
    const target = document.getElementById(id);
    if (target === null) return;
    const source = parsedHTML.getElementById(id);
    target.innerHTML = source ? source.innerHTML : '';
  }

  renderProductGridContainer(parsedHTML) {
    const id = 'ProductGridContainer';
    const target = document.getElementById(id);
    if (target === null) return;
    const source = parsedHTML.getElementById(id);
    target.innerHTML = source ? source.innerHTML : '';

    const layoutSwitcher = document.querySelector('layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  /* CUSTOM: harempants — INTENTIONALLY NO LONGER CALLED (see applyResponse). It
     wholesale-replaced the desktop sidebar (destroying its <form>, cache and control
     nodes every render); renderFilters now keeps the desktop groups in sync surgically.
     Kept defined so a future Hyper merge that still references it doesn't break. */
  renderFacetFiltersContainer(parsedHTML) {
    const id = 'FacetFiltersContainer';
    const target = document.getElementById(id);
    if (target === null) return;
    const source = parsedHTML.getElementById(id);
    target.innerHTML = source ? source.innerHTML : '';
  }

  renderProductCount(parsedHTML) {
    const id = 'ProductCount';
    const target = document.getElementById(id);
    if (target === null) return;
    const source = parsedHTML.getElementById(id);
    target.innerHTML = source ? source.innerHTML : '';
  }

  renderSortBy(parsedHTML) {
    const id = 'SortByContainer';
    const target = document.getElementById(id);
    if (target === null) return;
    const source = parsedHTML.getElementById(id);
    target.innerHTML = source ? source.innerHTML : '';
  }

  renderSortByMobile(parsedHTML) {
    const id = 'SortByContainerMobile';
    const target = document.getElementById(id);
    if (target === null) return;
    const source = parsedHTML.getElementById(id);
    target.innerHTML = source ? source.innerHTML : '';
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    /* CUSTOM: harempants — reuse the document applyResponse already parsed (parse-once);
       fall back to parsing responseText for any other publisher. */
    const parsedHTML = event.parsedHTML || new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(evt) {
    evt.preventDefault();

    if (this.facetEl.classList.contains('xl:block')) {
      this.facetEl.classList.remove('xl:block');
    } else {
      this.facetEl.classList.add('xl:block');
    }
  }
}
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    /* CUSTOM: harempants — delegate to the enhanced load-more controller
       (assets/custom-load-more.js) when present. It handles "Showing X of Y",
       URL/back restore, prefetch and auto-advance. Falls back to vendor
       behaviour if the controller is absent or declines. See
       plans/plan-collection-loadmore-ux.md. */
    if (window.CustomLoadMore && typeof window.CustomLoadMore.onButtonClick === 'function') {
      if (window.CustomLoadMore.onButtonClick(this) !== false) return;
    }
    /* END CUSTOM: harempants */
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}
customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });
    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);
